import {Component} from '@angular/core';
import {ROUTES} from './side-nav-routes.config';
import {ThemeConstantService} from '../../services/theme-constant.service';
import {AuthenticationService} from '../../services/authentication.service';
import {LicenseService} from '../../services/license.service';
import { AssetCategory, User } from "../../../../../../database-models";
import { firstValueFrom } from "rxjs";
import { IsActiveMatchOptions } from "@angular/router";
import { ApiService } from "../../../services/api.service";
import { SideNavInterface } from "../../interfaces/side-nav.type";

@Component({
    selector: 'app-sidenav',
    templateUrl: './side-nav.component.html',
    standalone: false
})

export class SideNavComponent {

  public menuItems: any[];
  isFolded: boolean;
  isSideNavDark: boolean;

  public assetCategories: AssetCategory[];

  constructor(
    private themeService: ThemeConstantService,
    private authenticationService: AuthenticationService,
    public licenseService: LicenseService,
    public api: ApiService
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.themeService.isMenuFoldedChanges.subscribe(isFolded => this.isFolded = isFolded);
    this.themeService.isSideNavDarkChanges.subscribe(isDark => this.isSideNavDark = isDark);

    // Menü bei jeder Änderung des Benutzers neu aufbauen (z.B. wenn nach dem Laden der Seite die aktuellen Rechte kommen)
    this.authenticationService.currentUser.subscribe(user => {
      if (user) {
        this.buildMenu(user);
      }
    });

    // Kategorien als Untermenü der Sachanlagen (nur mit Recht, sonst lehnt der Server ab)
    if (this.authenticationService.can('assets.view')) {
      this.assetCategories = await firstValueFrom(this.api.getAssetCategories());
      this.buildMenu(this.authenticationService.currentUserValue);
    }
  }

  private buildMenu(currentUser: User): void {
    const isVisible = (menuItem: SideNavInterface): boolean =>
      (!menuItem.adminOnly || currentUser.isAdmin)
      && (!menuItem.rights || this.authenticationService.can(...menuItem.rights))
      && (!menuItem.permissionSystem || !!currentUser.company?.user_object_permissions)
      && (!menuItem.moduleName || !!currentUser.company?.modules?.includes(menuItem.moduleName));

    // neue Objekte, damit die globale ROUTES-Konfiguration unverändert bleibt;
    // Bereiche mit Untermenü nur zeigen, wenn mindestens ein Unterpunkt erlaubt ist
    this.menuItems = ROUTES
      .filter(isVisible)
      .map(menuItem => ({...menuItem, submenu: menuItem.submenu.filter(isVisible)}))
      .filter(menuItem => menuItem.submenu.length > 0 || !ROUTES.find(route => route.title === menuItem.title)?.submenu.length);


    // Sachanlagen als neues Objekt ersetzen: Ohne Kategorien wurde der Eintrag bisher als einfacher Menüpunkt gerendert
    // und von routerLinkActive mit "ant-menu-item-selected" markiert. Wechselt der Eintrag danach zum Untermenü,
    // entfernt routerLinkActive diese alte Klasse nicht mehr und alle Kategorien erscheinen aktiv.
    // Durch das neue Objekt (track item) wird das <li> neu aufgebaut; zudem bleibt die globale ROUTES-Konfiguration unverändert.
    // Kategorie bleibt markiert, auch wenn weitere Parameter in der URL stehen (z.B. ?asset=… beim geöffneten Drawer)
    const categoryActiveOptions: IsActiveMatchOptions = {paths: 'exact', queryParams: 'subset', matrixParams: 'ignored', fragment: 'ignored'};
    this.menuItems = this.menuItems.map(menuItem => {
      if (menuItem.path !== '/sachanlagen' || !this.assetCategories) {
        return menuItem;
      }
      return {
        ...menuItem,
        routerLinkActiveOptions: categoryActiveOptions,
        submenu: this.assetCategories.map(assetCategory => ({
          title: assetCategory.name,
          iconType: 'lucide',
          icon: assetCategory.icon,
          submenu: [],
          path: `/sachanlagen`,
          routerLinkActiveOptions: categoryActiveOptions,
          queryParams: {
            category: assetCategory.id
          }
        } as SideNavInterface))
      };
    });

  }
}
